import * as React from 'react';
import { Translate, I18n } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { SearchDriveItemState } from '../../../../containers/DriveItemList/Search/reducer';
import { toggleSearchFieldVisibility } from '../../../../containers/DriveItemList/Search/actions';
import { DriveItemListOrigin, TagMarkers } from '../../../../containers/DriveItemListPage/constants';
import {
	SearchFieldContainer,
	SearchFieldHeader,
	SearchFieldHeaderSection,
	SearchFieldBody,
	SearchFieldBar,
	SearchFieldInput,
	SearchFieldIcon,
	ButtonClear,
	ButtonAction,
	SearchFieldHeaderLabel,
} from './styles';
import TagsList, { TagState } from '../TagsList';
import Utils from '../../../../utils';
import RoutesUtils from '../../../../utils/routes';
import keys from '../../../../utils/keys';

interface SearchFieldProps {
	searchText?: string;
	search: SearchDriveItemState;
	isTagsListDisplayed: boolean;
	availableTags: string[] | undefined;
	dispatch: Dispatch<StoreState>;
}

interface SearchFieldState {
	searchText: string;
	tags: TagState[];
}

class SearchField extends React.Component<SearchFieldProps, SearchFieldState> {
	private inputElement: HTMLElement;

	static getTagsFromSearchString(availableTags?: string[]): TagState[] {
		if (!availableTags) {
			return [];
		}

		return availableTags.map(tag => ({
			label: tag,
			isActive: false,
		}));
	}

	constructor(props: SearchFieldProps) {
		super(props);
		this.handleClearClick = this.handleClearClick.bind(this);
		this.handleCancelClick = this.handleCancelClick.bind(this);
		this.handleConfirmClick = this.handleConfirmClick.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleSearchTextChange = this.handleSearchTextChange.bind(this);
		this.handleTagToggle = this.handleTagToggle.bind(this);

		this.state = {
			searchText: '',
			tags: SearchField.getTagsFromSearchString(props.availableTags),
		};
	}

	componentDidMount() {
		window.addEventListener('keydown', this.handleKeyDown);
	}

	componentWillUnmount() {
		window.removeEventListener('keydown', this.handleKeyDown);
	}

	render() {
		const { isTagsListDisplayed } = this.props;
		const { searchText, tags } = this.state;

		return (
			<SearchFieldContainer className="SearchField">
				<SearchFieldHeader>
					<SearchFieldHeaderSection>
						<ButtonAction onClick={this.handleCancelClick}>
							<Translate value="messages.cancel" />
						</ButtonAction>
					</SearchFieldHeaderSection>
					<SearchFieldHeaderSection>
						<SearchFieldHeaderLabel value="messages.search" />
					</SearchFieldHeaderSection>
					<SearchFieldHeaderSection>
						<ButtonAction
							disabled={this.isSearchDisabled}
							onClick={this.handleConfirmClick}
						>
							<Translate value="messages.ok" />
						</ButtonAction>
					</SearchFieldHeaderSection>
				</SearchFieldHeader>
				<SearchFieldBody>
					<SearchFieldBar>
						<SearchFieldIcon>
							{Utils.getSvgIcon('iconset_uprez_menu_search')}
						</SearchFieldIcon>
						<SearchFieldInput
							ref={element => { this.inputElement = element; }}
							value={searchText}
							onChange={this.handleSearchTextChange}
							placeholder={I18n.t('messages.placeholder_search_drive_item')}
							autoFocus={true}
						/>
						{searchText &&
							<ButtonClear onClick={this.handleClearClick}>
								{Utils.getSvgIcon('iconset_close_bar')}
							</ButtonClear>
						}
					</SearchFieldBar>
					{isTagsListDisplayed && tags.length > 0 &&
						<TagsList
							tags={tags}
							onTagToggle={this.handleTagToggle}
						/>
					}
				</SearchFieldBody>
			</SearchFieldContainer>
		);
	}

	private get isSearchDisabled(): boolean {
		const { searchText, tags } = this.state;
		return !searchText && tags.every(({ isActive }) => !isActive);
	}

	private handleCancelClick(): void {
		this.props.dispatch(toggleSearchFieldVisibility());
	}

	private handleConfirmClick(): void {
		if (this.isSearchDisabled) {
			return;
		}

		const { dispatch } = this.props;
		const { searchText, tags } = this.state;

		let searchString = searchText;
		const activeTags = tags
			.filter(({ isActive }) => isActive)
			.map(({ label }) => label);

		if (activeTags.length > 0) {
			searchString += `${TagMarkers.Start}${activeTags.join(',')}${TagMarkers.End}`;
		}

		window.location.hash = `#${RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Search, encodeURIComponent(searchString))}`;
		dispatch(toggleSearchFieldVisibility());
	}

	private handleKeyDown(e: KeyboardEvent): void {
		if (keys.isEnter(e.key)) {
			e.preventDefault();
			this.handleConfirmClick();
		}

		if (keys.isEsc(e.key)) {
			e.preventDefault();
			this.handleCancelClick();
		}
	}

	private handleClearClick(e: React.MouseEvent<HTMLButtonElement>): void {
		e.preventDefault();
		this.setState({ searchText: '' });
		this.inputElement.focus();
	}

	private handleSearchTextChange(e: React.FormEvent<HTMLInputElement>): void {
		this.setState({ searchText: e.currentTarget.value });
	}

	private handleTagToggle(index: number): void {
		this.setState({
			tags: [
				...this.state.tags.slice(0, index),
				{
					...this.state.tags[index],
					isActive: !this.state.tags[index].isActive,
				},
				...this.state.tags.slice(index + 1),
			],
		});
	}
}

export default SearchField;




// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/DriveItemList/Search/Field/index.tsx